"use client";

import React, { useEffect } from "react";
import { Phone, Mail, MapPin, Clock, Send, CircleCheck } from "lucide-react";
import { toast } from "sonner";
import { Link } from "@/components/common/Link";
import { Button } from "@/components/ui/button";
import { Form, FormControl, FormDescription, FormField, FormItem, FormLabel, FormMessage } from "@/components/ui/form";
import { Toaster } from "@/components/ui/sonner";
import { FormProvider, useWvcForm } from "@/integrations/forms/FormProvider";
const businessPhone = "1-800-RTD-CO-12";
const businessEmail = "solutions@customrtd.com";
const businessAddress = "Northeast Ohio";
const businessHours = "Production: Mon - Fri: 7am - 10pm";
const ContactForm = () => {
  const {
    isSubmitting,
    isSubmitted,
    submitError,
    successMessage,
    handleSubmit,
    control,
    resetForm
  } = useWvcForm();
  useEffect(() => {
    if (isSubmitted && !submitError) {
      toast.success(successMessage || "Inquiry sent successfully!");
    }
  }, [isSubmitted, submitError, successMessage]);
  if (isSubmitted && !submitError) {
    return <div data-section-id="38779" className="flex flex-col items-center justify-center h-full min-h-[400px] bg-accent/30 border border-border rounded-lg p-8 text-center animate-in fade-in zoom-in duration-300">
        <div className="size-16 bg-primary/20 text-primary rounded-lg flex items-center justify-center mb-6">
          <CircleCheck className="size-8" />
        </div>
        <h3 className="text-2xl font-sans font-bold text-foreground mb-2">Inquiry Received</h3>
        <p className="text-muted-foreground mb-8 max-w-md">
          {successMessage}
        </p>
        <Button onClick={resetForm} variant="outline" className="rounded-lg border-primary text-primary hover:bg-primary hover:text-primary-foreground">
          Start New Inquiry
        </Button>
      </div>;
  }
  return <Form onSubmit={handleSubmit} className="space-y-5">
      <div className="grid grid-cols-1 md:grid-cols-2 gap-5">
        <FormField control={control} name="name" rules={{
        required: "Full name is required"
      }} render={({
        field
      }) => <FormItem>
              <FormLabel className="text-foreground font-mono text-xs uppercase tracking-wider">Full Name <span className="text-primary">*</span></FormLabel>
              <FormControl>
                <input className="flex h-11 w-full rounded-md border border-input bg-background px-3 py-2 text-sm ring-offset-background file:border-0 file:bg-transparent file:text-sm file:font-medium placeholder:text-muted-foreground focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2 disabled:cursor-not-allowed disabled:opacity-50 transition-all" placeholder="Jane Smith" {...field} />
              </FormControl>
              <FormMessage />
            </FormItem>} />

        <FormField control={control} name="email" rules={{
        required: "Email is required",
        pattern: {
          value: /^[^\s@]+@[^\s@]+\.[^\s@]+$/,
          message: "Invalid email address"
        }
      }} render={({
        field
      }) => <FormItem>
              <FormLabel className="text-foreground font-mono text-xs uppercase tracking-wider">Business Email <span className="text-primary">*</span></FormLabel>
              <FormControl>
                <input type="email" className="flex h-11 w-full rounded-md border border-input bg-background px-3 py-2 text-sm ring-offset-background file:border-0 file:bg-transparent file:text-sm file:font-medium placeholder:text-muted-foreground focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2 disabled:cursor-not-allowed disabled:opacity-50 transition-all" placeholder="jane@brand.com" {...field} />
              </FormControl>
              <FormMessage />
            </FormItem>} />
      </div>

      <div className="grid grid-cols-1 md:grid-cols-2 gap-5">
        <FormField control={control} name="phone" render={({
        field
      }) => <FormItem>
              <FormLabel className="text-foreground font-mono text-xs uppercase tracking-wider">Direct Line</FormLabel>
              <FormControl>
                <input type="tel" className="flex h-11 w-full rounded-md border border-input bg-background px-3 py-2 text-sm ring-offset-background file:border-0 file:bg-transparent file:text-sm file:font-medium placeholder:text-muted-foreground focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2 disabled:cursor-not-allowed disabled:opacity-50 transition-all" placeholder="+1 (555) 000-0000" {...field} />
              </FormControl>
              <FormDescription className="text-[10px] font-mono">Optional – best for rapid technical consultation</FormDescription>
              <FormMessage />
            </FormItem>} />

        <FormField control={control} name="contact_time" render={({
        field
      }) => <FormItem>
              <FormLabel className="text-foreground font-mono text-xs uppercase tracking-wider">Product Category</FormLabel>
              <FormControl>
                <div className="relative">
                  <select className="flex h-11 w-full rounded-md border border-input bg-background px-3 py-2 text-sm ring-offset-background placeholder:text-muted-foreground focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2 disabled:cursor-not-allowed disabled:opacity-50 appearance-none transition-all" {...field} value={field.value || ""}>
                    <option value="" disabled>Select a category</option>
                    <option value="Probiotic Fizz">Probiotic Fizz</option>
                    <option value="Adaptogen Seltzer">Adaptogen Seltzer</option>
                    <option value="Electrolyte Recovery">Electrolyte Recovery</option>
                    <option value="Custom Formulation">Custom Formulation</option>
                  </select>
                  <div className="pointer-events-none absolute inset-y-0 right-0 flex items-center px-3 text-muted-foreground">
                    <svg className="h-4 w-4 fill-current" xmlns="http://www.w3.org/2000/svg" viewBox="0 0 20 20">
                      <path d="M9.293 12.95l.707.707L15.657 8l-1.414-1.414L10 10.828 5.757 6.586 4.343 8z" />
                    </svg>
                  </div>
                </div>
              </FormControl>
              <FormMessage />
            </FormItem>} />
      </div>

      <FormField control={control} name="message" render={({
      field
    }) => <FormItem>
            <FormLabel className="text-foreground font-mono text-xs uppercase tracking-wider">Project Scope & Volume</FormLabel>
            <FormControl>
              <textarea className="flex min-h-[120px] w-full rounded-md border border-input bg-background px-3 py-2 text-sm ring-offset-background placeholder:text-muted-foreground focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2 disabled:cursor-not-allowed disabled:opacity-50 resize-y transition-all" placeholder="Tell us about your formulation goals and estimated annual volume..." {...field} />
            </FormControl>
            <FormMessage />
          </FormItem>} />

      {submitError && <div className="p-4 bg-destructive/10 border border-destructive/20 rounded-md text-destructive text-sm font-mono">
          {submitError}
        </div>}

      <Button type="submit" disabled={isSubmitting} className="w-full h-12 text-base font-bold bg-primary hover:bg-primary/90 text-primary-foreground rounded-lg shadow-lg transition-all">
        {isSubmitting ? <>Transmitting Data...</> : <>
            Initiate Project <Send className="ml-2 size-4" />
          </>}
      </Button>
    </Form>;
};
export default function DirectConnection() {
  const contactMethods = [{
    icon: Mail,
    label: "Partnerships",
    value: businessEmail,
    href: `mailto:${businessEmail}`,
    color: "text-chart-3",
    bg: "bg-chart-3/10"
  }];
  return <section id="lets-build-your-brand" className="bg-background py-20 lg:py-28 relative overflow-hidden">
      {}
      <div className="absolute inset-0 pointer-events-none opacity-[0.05]" style={{
      backgroundImage: "linear-gradient(to right, #000 1px, transparent 1px), linear-gradient(to bottom, #000 1px, transparent 1px)",
      backgroundSize: "60px 60px"
    }} />

      <div className="container mx-auto px-4 relative z-10">
        <div className="grid lg:grid-cols-2 gap-12 lg:gap-20 items-start">
          
          {}
          <div className="space-y-10">
            <div className="space-y-4">
              <h2 className="text-4xl md:text-5xl font-sans font-bold text-foreground tracking-tighter">
                Let's Build Your <span className="text-primary">Brand</span>
              </h2>
              <p className="text-lg text-muted-foreground max-w-md leading-relaxed">
                Ready to scale your beverage brand? Partner with Custom RTD Co. for elite formulation and precision counter-pressure canning.
              </p>
            </div>

            <div className="space-y-6">
              {contactMethods.map((method, i) => <Link key={i} to={method.href} className="group flex items-center gap-6 p-6 rounded-xl border border-border bg-card hover:border-primary/40 hover:shadow-xl transition-all duration-400" data-index={i}>
                  <div className={`size-16 rounded-lg flex items-center justify-center shrink-0 ${method.bg} ${method.color} transition-transform group-hover:scale-110`}>
                    <method.icon className="size-8" strokeWidth={1.5} />
                  </div>
                  <div>
                    <p className="text-xs font-mono font-medium text-muted-foreground uppercase tracking-widest mb-1">
                      {method.label}
                    </p>
                    <p className="text-xl md:text-2xl font-sans font-bold text-foreground group-hover:text-primary transition-colors">
                      {method.value}
                    </p>
                  </div>
                </Link>)}
            </div>

            <div className="grid sm:grid-cols-2 gap-6 pt-4">
              <div className="flex items-start gap-4">
                <div className="mt-1 size-10 rounded-lg bg-primary/5 flex items-center justify-center shrink-0 text-primary">
                  <MapPin className="size-5" />
                </div>
                <div>
                  <h4 className="font-sans font-bold text-foreground mb-1">Location</h4>
                  <p className="text-muted-foreground text-sm">{businessAddress}</p>
                </div>
              </div>
              <div className="flex items-start gap-4">
                <div className="mt-1 size-10 rounded-lg bg-primary/5 flex items-center justify-center shrink-0 text-primary">
                  <Clock className="size-5" />
                </div>
                <div>
                  <h4 className="font-sans font-bold text-foreground mb-1">Operations</h4>
                  <p className="text-muted-foreground text-sm">{businessHours}</p>
                </div>
              </div>
            </div>
          </div>

          {}
          <div className="bg-card/50 backdrop-blur-md border border-border rounded-xl shadow-2xl p-6 md:p-8 lg:p-10 relative">
            <div className="absolute top-0 left-0 w-full h-1.5 bg-primary rounded-t-xl" />
            
            <div className="mb-8">
              <h3 className="text-2xl font-sans font-bold text-foreground mb-2">Start Your Formulation</h3>
              <p className="text-muted-foreground">
                Submit your project specifications and our technical team will reach out within 24 hours.
              </p>
            </div>

            <FormProvider formKey="build_brand_form_contact" formId="build_brand_form_contact" sectionName="direct-connection" defaultValues={{
            name: "",
            email: "",
            phone: "",
            contact_time: "",
            message: ""
          }} validationRules={{
            name: {
              required: true
            },
            email: {
              required: true,
              pattern: "email"
            }
          }} submitText="Initiate Project" successMessage="Thank you! Our formulation experts have received your inquiry and will contact you shortly.">
              <ContactForm />
            </FormProvider>
          </div>

        </div>
      </div>
      <Toaster position="top-right" />
    </section>;
}